import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { ArrowLeft, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';

interface AffiliateRouteProps {
  children: React.ReactNode;
}

export const AffiliateRoute: React.FC<AffiliateRouteProps> = ({ children }) => {
  const { user, loading: authLoading, hasSubscription } = useAuth();
  const [isAffiliate, setIsAffiliate] = useState<boolean | null>(null);
  const [affiliateLoading, setAffiliateLoading] = useState(true);
  const location = useLocation();
  
  // Effect to check affiliate status
  useEffect(() => {
    const checkAffiliateStatus = async () => {
      console.log('🔍 AffiliateRoute: Starting affiliate check...', { user: !!user, authLoading });
      
      if (authLoading) {
        console.log('🔍 AffiliateRoute: Auth still loading, waiting...');
        return;
      }
      
      if (!user || !user.email) {
        console.log('🔍 AffiliateRoute: No user or email found, setting affiliate to false');
        setIsAffiliate(false);
        setAffiliateLoading(false);
        return;
      }

      try {
        console.log('🔍 AffiliateRoute: Checking affiliate status for email:', user.email);
        
        // Query by email instead of ID
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_affiliate')
          .eq('email', user.email.toLowerCase())
          .single();
          
        if (profileError) {
          console.error('❌ AffiliateRoute: Profile query failed:', profileError);
          setIsAffiliate(false);
        } else {
          console.log('✅ AffiliateRoute: Profile data retrieved:', profileData);
          const affiliateStatus = !!profileData?.is_affiliate;
          console.log('✅ AffiliateRoute: Affiliate status:', affiliateStatus);
          setIsAffiliate(affiliateStatus);
        }
      } catch (err) {
        console.error('❌ AffiliateRoute: Failed to check affiliate status:', err);
        setIsAffiliate(false);
      } finally {
        console.log('🔍 AffiliateRoute: Affiliate check completed');
        setAffiliateLoading(false);
      }
    };

    checkAffiliateStatus();
  }, [user, authLoading]);
  
  // Determine if we're still loading
  const isLoading = authLoading || affiliateLoading;
  
  console.log('🔍 AffiliateRoute: Current state:', { isLoading, user: !!user, hasSubscription, isAffiliate });
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }
  
  // Not logged in
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  
  // Not subscribed
  if (!hasSubscription) {
    return <Navigate to="/pricing" replace />;
  }
  
  // Not an affiliate
  if (!isAffiliate) {
    console.log('❌ AffiliateRoute: Access denied - user is not an affiliate');
    return (
      <div className="min-h-screen bg-white">
        {/* Atmospheric lighting effects */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-primary-500/10 blur-[120px] -z-10"></div>
        <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gray-300/20 blur-[120px] -z-10 translate-y-1/3"></div>
        
        <div className="container mx-auto py-8 px-4">
          <div className="mb-8">
            <Button 
              as={Link} 
              to="/app/calendar" 
              variant="ghost" 
              className="flex items-center text-gray-700 hover:text-gray-950 transition-colors"
            >
              <ArrowLeft size={18} className="mr-1" />
              Back to Dashboard
            </Button>
          </div>
          
          <div className="flex flex-col items-center justify-center max-w-md mx-auto">
            <div className="glass-card border border-red-200/50 bg-white/30 backdrop-blur-[100px] shadow-lg rounded-xl p-8 w-full">
              <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
              <p className="text-gray-700 mb-6">
                Your account doesn't have access to the affiliate portal. This area is only available to approved affiliates.
              </p>
              <div className="flex flex-col gap-4">
                <Button 
                  as="a"
                  href="https://simplifiedtradingjournal.com/team"
                  target="_blank"
                  className="bg-primary-600 hover:bg-primary-700 text-white font-medium !rounded-full shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <Users size={18} className="mr-2" />
                  Learn About Our Affiliate Program
                </Button>
                
                <Button 
                  as={Link}
                  to="/app/calendar"
                  className="bg-black hover:bg-gray-900 text-white font-medium !rounded-full shadow-md hover:shadow-lg transition-all duration-300"
                >
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // If we get here, user is an affiliate
  console.log('✅ AffiliateRoute: Access granted - user is an affiliate');
  return <>{children}</>;
};